import { Box, Button, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import IconifyIcon from 'components/base/IconifyIcon';
import { PLAN_CONFIG, PlanType } from 'data/plan-config';
import { weddingPaths } from 'routes/paths';

interface SidenavUpgradeCardProps {
  storageUsed: number;
  storageLimit: number;
  plan: string;
  collapsed?: boolean;
  expired?: boolean;
  /**
   * Gallery photos vs the plan's photo cap. When capped (e.g. Blossom's 50),
   * the meter counts photos — what actually runs out — instead of storage.
   */
  photoCap?: { count: number; cap: number | null };
}

const SidenavUpgradeCard = ({
  storageUsed,
  storageLimit,
  plan,
  collapsed = false,
  expired = false,
  photoCap,
}: SidenavUpgradeCardProps) => {
  const { t } = useTranslation();
  const photoLimit = photoCap?.cap ?? null;
  const percentage =
    photoLimit !== null && photoCap
      ? Math.min((photoCap.count / photoLimit) * 100, 100)
      : storageLimit > 0
        ? Math.min((storageUsed / storageLimit) * 100, 100)
        : 0;
  const isFree = plan === 'free';
  const hasSignificantUsage = percentage >= 10;

  // Collapsed state: just a glowing icon with tooltip
  if (collapsed) {
    return (
      <Tooltip
        title={expired ? t('nav.upgrade.tooltip_renew') : t('nav.upgrade.tooltip_upgrade')}
        placement="right"
        arrow
      >
        <Box
          component={Link}
          to={weddingPaths.coupleBilling}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: expired
              ? 'error.lighter'
              : percentage >= 90
                ? 'error.lighter'
                : percentage >= 70
                  ? 'warning.lighter'
                  : 'primary.lighter',
            color: expired
              ? 'error.main'
              : percentage >= 90
                ? 'error.main'
                : percentage >= 70
                  ? 'warning.main'
                  : 'primary.main',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { opacity: 0.85 },
          }}
        >
          <IconifyIcon icon="solar:bolt-bold-duotone" fontSize={20} />
        </Box>
      </Tooltip>
    );
  }

  // Free plan with low/no usage: show teaser instead of storage bar
  if (isFree && !hasSignificantUsage) {
    return (
      <Box
        component={Link}
        to={weddingPaths.coupleBilling}
        sx={{
          mx: 2,
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          bgcolor: 'background.elevation1',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.3s',
          textDecoration: 'none',
          cursor: 'pointer',
          display: 'block',
          '&:hover': { opacity: 0.85 },
        }}
      >
        {/* Plan name */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <IconifyIcon
            icon="solar:bolt-bold-duotone"
            fontSize={16}
            sx={{ color: 'primary.main' }}
          />
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {PLAN_CONFIG[plan as PlanType]?.displayName ?? plan}
          </Typography>
        </Stack>

        {/* Teaser */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('nav.upgrade.teaser')}
        </Typography>

        {/* Upgrade button */}
        <Button
          variant="contained"
          size="small"
          fullWidth
          color="primary"
          sx={{ fontWeight: 700, py: 0.8 }}
          startIcon={<IconifyIcon icon="solar:rocket-2-bold-duotone" fontSize={14} />}
        >
          {t('nav.upgrade.button_upgrade')}
        </Button>
      </Box>
    );
  }

  // Default: storage-aware card (expired, high usage, or paid plans)
  const usedGB = (storageUsed / (1024 * 1024 * 1024)).toFixed(1);
  const limitGB = (storageLimit / (1024 * 1024 * 1024)).toFixed(0);
  const progressColor = percentage >= 90 ? 'error' : percentage >= 70 ? 'warning' : 'primary';

  return (
    <Box
      component={Link}
      to={weddingPaths.coupleBilling}
      sx={{
        mx: 2,
        mb: 3,
        p: 2.5,
        borderRadius: 3,
        bgcolor: 'background.elevation1',
        border: '1px solid',
        borderColor: expired ? 'error.light' : percentage >= 90 ? 'error.light' : 'divider',
        transition: 'all 0.3s',
        textDecoration: 'none',
        cursor: 'pointer',
        display: 'block',
        '&:hover': { opacity: 0.85 },
      }}
    >
      {/* Header row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconifyIcon
            icon={expired ? 'solar:danger-triangle-bold-duotone' : 'solar:bolt-bold-duotone'}
            fontSize={16}
            sx={{
              color: expired ? 'error.main' : percentage >= 90 ? 'error.main' : 'primary.main',
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {PLAN_CONFIG[plan as PlanType]?.displayName ?? plan}
            {expired ? t('nav.upgrade.expired_suffix') : ''}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: percentage >= 90 ? 'error.main' : 'text.disabled', fontWeight: 600 }}
        >
          {percentage.toFixed(0)}%
        </Typography>
      </Stack>

      {/* Storage bar */}
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={progressColor}
        sx={{
          height: 5,
          borderRadius: 4,
          mb: 1,
          bgcolor: 'background.elevation3',
          '& .MuiLinearProgress-bar': { borderRadius: 4 },
        }}
      />

      {/* Storage label */}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2 }}>
        {photoLimit !== null && photoCap
          ? t('nav.upgrade.photos_used', { used: photoCap.count, limit: photoLimit })
          : t('nav.upgrade.storage_used', { used: usedGB, limit: limitGB })}
      </Typography>

      {/* Upgrade / Renew button */}
      <Button
        variant="contained"
        size="small"
        fullWidth
        color={expired ? 'error' : percentage >= 90 ? 'error' : 'primary'}
        sx={{ fontWeight: 700, py: 0.8 }}
        startIcon={
          <IconifyIcon
            icon={expired ? 'solar:refresh-bold-duotone' : 'solar:rocket-2-bold-duotone'}
            fontSize={14}
          />
        }
      >
        {expired
          ? t('nav.upgrade.button_renew')
          : percentage >= 90
            ? t('nav.upgrade.button_upgrade_now')
            : t('nav.upgrade.button_upgrade')}
      </Button>
    </Box>
  );
};

export default SidenavUpgradeCard;
